import React, { useState, useEffect } from "react";
import { Card, Table, Badge, Button, Form, InputGroup, Row, Col, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import { getAllSupports, updateSupportStatus, deleteSupport, getSupportAttachmentUrl } from "../../../services/SupportService";
import { userProfile } from "../../../services/AuthService";
import { toast } from "react-toastify";
import { Colors } from "../../../contants/Colors";
import {
  SUPPORT_TYPES,
  SUPPORT_TYPE_BADGE_COLORS,
  getSupportTypeDisplay,
  getSupportPriorityDisplay,
  getSupportIssueStartedDisplay,
  getSupportContactMethodDisplay,
} from "../../../contants/supportTypes";

const SupportList = () => {
  const navigate = useNavigate();
  const [supports, setSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedSupport, setSelectedSupport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const checkAccessAndFetch = async () => {
      try {
        const userData = await userProfile();
        if (userData?.role !== "superAdmin") {
          toast.error("Access denied. Only superadmin can view support requests.");
          navigate("/dashboard");
          return;
        }
        await fetchSupports();
      } catch (error) {
        console.error("Error checking user access:", error);
        navigate("/dashboard");
      }
    };
    checkAccessAndFetch();
  }, [fetchSupports, navigate]);

  const fetchSupports = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllSupports(page, 10, search, typeFilter, statusFilter);
      setSupports(response.supports || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error) {
      toast.error(error.message || "Failed to fetch support requests");
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateSupportStatus(id, newStatus);
      toast.success("Status updated successfully");
      fetchSupports();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this support request?")) {
      try {
        await deleteSupport(id);
        toast.success("Support request deleted successfully");
        fetchSupports();
      } catch (error) {
        toast.error(error.message || "Failed to delete support request");
      }
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "warning",
      in_progress: "info",
      resolved: "success",
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const getTypeBadge = (supportOrType, customType = "", subcategory = "", customSubcategory = "") => {
    const type = typeof supportOrType === "object" ? supportOrType?.type : supportOrType;
    const resolvedCustomType =
      typeof supportOrType === "object" ? supportOrType?.customType || "" : customType;
    const resolvedSubcategory =
      typeof supportOrType === "object" ? supportOrType?.subcategory || "" : subcategory;
    const resolvedCustomSubcategory =
      typeof supportOrType === "object"
        ? supportOrType?.customSubcategory || ""
        : customSubcategory;

    return (
      <Badge bg={SUPPORT_TYPE_BADGE_COLORS[type] || "secondary"}>
        {getSupportTypeDisplay(
          type,
          resolvedCustomType,
          resolvedSubcategory,
          resolvedCustomSubcategory
        )}
      </Badge>
    );
  };

  return (
    <>
      <PageTitle motherMenu="Support" activeMenu="Support Requests" />
      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header>
              <Card.Title>Support Requests ({total})</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={4}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={typeFilter}
                    onChange={(e) => {
                      setTypeFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Types</option>
                    {SUPPORT_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.emoji} {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="primary"
                    onClick={fetchSupports}
                    style={{ backgroundColor: Colors.primary, borderColor: Colors.primary }}
                  >
                    Refresh
                  </Button>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">Loading...</div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table-responsive-sm">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Priority</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supports.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              No support requests found
                            </td>
                          </tr>
                        ) : (
                          supports.map((support) => (
                            <tr key={support._id}>
                              <td>
                                {support.user?.firstName} {support.user?.lastName}
                              </td>
                              <td>{getSupportPriorityDisplay(support.priority)}</td>
                              <td>{getTypeBadge(support)}</td>
                              <td>
                                <div style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {support.description.substring(0, 100)}
                                  {support.description.length > 100 && "..."}
                                </div>
                              </td>
                              <td>{getStatusBadge(support.status)}</td>
                              <td>{new Date(support.createdAt).toLocaleDateString()}</td>
                              <td>
                                <Button
                                  variant="info"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => {
                                    setSelectedSupport(support);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  View
                                </Button>
                                <Form.Select
                                  size="sm"
                                  value={support.status}
                                  onChange={(e) => handleStatusChange(support._id, e.target.value)}
                                  style={{ display: "inline-block", width: "auto" }}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="resolved">Resolved</option>
                                </Form.Select>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="ms-1"
                                  onClick={() => handleDelete(support._id)}
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing page {page} of {totalPages} (Total: {total})
                    </div>
                    <div>
                      <Button
                        variant="outline-primary"
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className="me-2"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline-primary"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Support Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSupport && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div style={{ marginBottom: "16px" }}>
                    <strong>User:</strong> {selectedSupport.user?.firstName} {selectedSupport.user?.lastName}
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <strong>Priority:</strong> {getSupportPriorityDisplay(selectedSupport.priority)}
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <strong>Issue started:</strong>{" "}
                    {getSupportIssueStartedDisplay(selectedSupport.issueStarted)}
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <strong>Preferred contact:</strong>{" "}
                    {getSupportContactMethodDisplay(selectedSupport.preferredContactMethod)}
                  </div>
                </Col>
                <Col md={6}>
                  <strong>Type:</strong> {getTypeBadge(selectedSupport)}
                  {selectedSupport.type === "other" && selectedSupport.customType && (
                    <>
                      <br />
                      <small className="text-muted">Custom Type: {selectedSupport.customType}</small>
                    </>
                  )}
                  <br />
                  <strong>Status:</strong> {getStatusBadge(selectedSupport.status)}
                  <br />
                  <strong>Date:</strong> {new Date(selectedSupport.createdAt).toLocaleString()}
                </Col>
              </Row>
              <hr />
              <div className="mb-3">
                <strong>Description:</strong>
                <p className="mt-2">{selectedSupport.description}</p>
              </div>
              {selectedSupport.recommendation && (
                <div className="mb-3">
                  <strong>Recommendation:</strong>
                  <p className="mt-2">{selectedSupport.recommendation}</p>
                </div>
              )}
              {selectedSupport.attachments?.length > 0 && (
                <div className="mb-3">
                  <strong>Pièces jointes:</strong>
                  <ul className="mt-2 mb-0">
                    {selectedSupport.attachments.map((attachment) => (
                      <li key={attachment.filename}>
                        <a
                          href={getSupportAttachmentUrl(attachment.filename)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {attachment.originalName || attachment.filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SupportList;

